import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { consumeInitialRoute } from "@/daily/challengeRoute";
import { consumeAudioLabRoute } from "@/qa/audioLabRoute";
import { getAudioManager } from "@/audio/AudioManager";
import { ENDLESS_RUN_CONTEXT } from "@/endless/types";
import { trackPlayNowClicked } from "@/analytics/gameAnalytics";
import { drawBrandMark } from "@/ui/brandMark";
import { attachOrientationGate, destroyLayoutChildren, type OrientationGateHandle } from "@/scenes/OrientationGate";
import { createLegalFooter } from "@/ui/LegalFooter";

/**
 * Instant Play & Onboarding V1: the lightweight landing screen a normal
 * homepage visit shows, for both new and returning players - SnakeScore
 * branding, a single `PLAY NOW` (starts an Endless Growth run
 * immediately), and a secondary `MAIN MENU` link to the existing full
 * menu (`MenuScene`, unchanged).
 *
 * Registered as `main.ts`'s scene index 0, ahead of `MenuScene` - this is
 * now the app's actual entry point, so it - not `MenuScene` - owns the
 * one-shot initial-route detection (`consumeInitialRoute`/
 * `consumeAudioLabRoute`) that used to live in `MenuScene.create()`.
 * Route/deep-link detection runs FIRST, before any normal-entry UI is
 * built, exactly as it did in `MenuScene` - a Beat My Score link, Score
 * Challenge QA preset, or Audio Lab QA route is never shown this screen
 * at all, never mind redirected into it after the fact. `MenuScene`
 * itself no longer performs this check (see its own doc) - a player only
 * ever reaches it via an explicit `MAIN MENU`/back navigation after this
 * one-shot gate has already run for the page load.
 */
export class EntryScene extends Phaser.Scene {
  /** Guards PLAY NOW against firing twice - a queued pointer click landing after an Enter/Space activation (or vice versa), or a double keypress - starts at most one run per screen. */
  private hasActivatedPlayNow = false;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("EntryScene");
  }

  create(): void {
    this.hasActivatedPlayNow = false;

    // Same one-shot gate, same precedence, same behavior as the code this
    // replaces in `MenuScene.create()` - see that module's own doc history
    // and `daily/challengeRoute.ts`'s `consumeInitialRoute()` for why this
    // must run before anything else in this scene.
    const initialRoute = consumeInitialRoute(window.location.pathname, window.location.search, window.location.hostname);
    if (initialRoute?.type === "token") {
      this.scene.start("ScoreChallengeScene", { token: initialRoute.token });
      return;
    }
    if (initialRoute?.type === "qaPreset") {
      this.scene.start("ScoreChallengeScene", { qaPreset: initialRoute.preset });
      return;
    }
    if (consumeAudioLabRoute(window.location.hostname, window.location.search)) {
      void import("@/scenes/AudioLabScene").then(({ AudioLabScene }) => {
        if (!this.scene.get("AudioLabScene")) this.scene.add("AudioLabScene", AudioLabScene);
        this.scene.start("AudioLabScene");
      });
      return;
    }

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.buildLayout();
    this.scale.on("resize", this.buildLayout, this);
    const onEnter = () => this.activatePlayNow();
    this.input.keyboard?.on("keydown-ENTER", onEnter);
    this.input.keyboard?.on("keydown-SPACE", onEnter);
    // Unlocks audio the instant ANY pointer/touch interaction with this
    // scene begins - synchronously, inside the raw `pointerdown` event
    // itself, never waiting on `createButton()`'s own ~80ms press
    // animation before its `onClick` fires. `unlock()` is idempotent (see
    // `AudioManager`'s own doc), so this is always safe to call again
    // inside `activatePlayNow()` below too - that call is what covers the
    // Enter/Space keyboard path, which has no pointer event to hook.
    const onPointerDown = () => getAudioManager().unlock();
    this.input.on("pointerdown", onPointerDown);
    this.events.once("shutdown", () => {
      this.scale.off("resize", this.buildLayout, this);
      this.input.keyboard?.off("keydown-ENTER", onEnter);
      this.input.keyboard?.off("keydown-SPACE", onEnter);
      this.input.off("pointerdown", onPointerDown);
    });

    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();
  }

  private buildLayout(): void {
    destroyLayoutChildren(this);
    const { width, height } = this.scale;
    const centerX = width / 2;

    const titleSize = Math.max(36, Math.min(64, width * 0.1));
    const title = this.add
      .text(centerX, height * 0.32, "SNAKESCORE", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    // PR27: the small snake+apple brand mark (the site's own favicon
    // shapes, see `drawBrandMark()`'s doc) - centered directly ABOVE
    // SNAKESCORE, never beside it, so it never competes with the title
    // for horizontal room on a narrow viewport, and sized off the
    // title's own font size so it scales with it. Every other line/button
    // below still anchors off `title.y`, unchanged, so this never nudges
    // CHOOSE YOUR BITE or the buttons.
    const brandMarkSize = titleSize * 0.6;
    const brandMarkGraphics = this.add.graphics();
    drawBrandMark(brandMarkGraphics, centerX, title.y - titleSize * 0.72 - brandMarkSize / 2, brandMarkSize);

    const taglineSize = Math.max(18, Math.min(28, width * 0.05));
    const tagline = this.add
      .text(centerX, title.y + titleSize * 0.74, "CHOOSE YOUR BITE", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${taglineSize}px`,
        fontStyle: "bold",
        color: "#5b8cff",
      })
      .setOrigin(0.5);

    const supportingY = tagline.y + taglineSize * 1.1;
    const supporting = this.add
      .text(centerX, supportingY, "ENDLESS — GROW AS LONG AS YOU CAN", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    const playButtonWidth = Math.min(280, width - 64);
    const playButtonY = supporting.y + 70;
    createButton(this, centerX, playButtonY, "PLAY NOW", () => this.activatePlayNow(), {
      width: playButtonWidth,
      height: 64,
      fontSize: "24px",
    });

    createButton(this, centerX, playButtonY + 84, "MAIN MENU", () => this.openMainMenu(), {
      width: 220,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });

    // Launch requirement (PR33): the discreet Privacy/Terms/Contact
    // footer - anchored to the very bottom edge, well below PLAY
    // NOW/MAIN MENU, so it's reachable from the very first screen
    // without competing with Instant Play for attention. See
    // `LegalFooter.ts`'s own doc.
    createLegalFooter(this);
  }

  /**
   * Runs entirely inside the synchronous click/keypress handler, so the
   * audio-unlock call lands inside the same user-gesture call stack the
   * browser's autoplay policy requires - `createButton`'s own shared
   * click handler never calls `unlock()` itself (only `onButtonClick()`'s
   * confirmation sound, gated on audio already being unlocked), so this
   * scene calls it explicitly, exactly like the QA Audio Lab already
   * does. `play_now_clicked` fires before the scene transition, so it is
   * never lost even in the (unreachable in practice) case the transition
   * itself somehow fails.
   */
  private activatePlayNow(): void {
    if (this.hasActivatedPlayNow) return;
    this.hasActivatedPlayNow = true;
    getAudioManager().unlock();
    trackPlayNowClicked();
    this.scene.start("GameScene", { endless: ENDLESS_RUN_CONTEXT, runSource: "play", entrySource: "instant_play" });
  }

  private openMainMenu(): void {
    this.scene.start("MenuScene");
  }
}
